<template>
  <div class="tag-filter">
    <a-tag
      v-for="option in visibleOptions"
      :key="option.option_id"
      :class="['filter-tag', { 'filter-tag-active': isSelected(option.option_value) }]"
      :color="isSelected(option.option_value) ? '#1890ff' : 'default'"
      @click="handleClick(option.option_value, option.option_name)"
    >
      {{ option.option_name }}
    </a-tag>
    <a-button
      v-if="hasMore"
      type="link"
      size="small"
      class="more-button"
      @click="toggleExpand"
    >
      {{ isExpanded ? '收起' : '更多' }}
    </a-button>
  </div>
</template>

<script>
import { isMobileDevice } from '@/utils/deviceDetection'

export default {
  name: 'TagFilter',
  props: {
    options: {
      type: Array,
      required: true,
      default: () => []
    },
    modelValue: {
      type: [String, Array],
      default: () => []
    },
    showMoreCount: {
      type: Number,
      default: 10
    },
    single: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      isExpanded: false,
      isMobile: false
    }
  },
  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile)
  },
  computed: {
    visibleOptions() {
      if (this.isExpanded) {
        return this.options
      }
      return this.options.slice(0, this.showMoreCount)
    },
    hasMore() {
      return this.options.length > this.showMoreCount
    },
    // 将 modelValue 统一转换为数组格式
    selectedValues() {
      if (this.single) {
        // 单选模式：显式判断空值，避免 option_value 为 0 时被当成未选
        const raw = this.modelValue
        if (raw === '' || raw === null || raw === undefined) {
          return []
        }
        // 兼容误存成数组的 radio 值（只取第一项参与选中判断）
        if (Array.isArray(raw)) {
          if (raw.length === 0) return []
          return [raw[0]]
        }
        return [raw]
      }
      // 多选模式：保持数组格式
      if (Array.isArray(this.modelValue)) {
        return this.modelValue
      }
      // 如果是字符串，转换为数组（兼容旧数据）；不能用 truthy 判断，0 也是合法 option_value
      if (this.modelValue === '' || this.modelValue === null || this.modelValue === undefined) {
        return []
      }
      return [this.modelValue]
    },
    // 找到"全部"选项
    allOption() {
      return this.options.find(opt => {
        const name = String(opt.option_name || '').trim()
        const value = opt.option_value
        const valueStr = String(value || '').toLowerCase().trim()
        return name === '全部' || 
               valueStr === 'all' ||
               value === '' || 
               value === null || 
               value === undefined
      })
    },
    // 找到"其他"选项
    otherOption() {
      return this.options.find(opt => {
        const name = String(opt.option_name || '').trim()
        const valueStr = String(opt.option_value || '').toLowerCase().trim()
        return name === '其他' || 
               valueStr === 'other' ||
               valueStr === 'others'
      })
    }
  },
  methods: {
    /** 单选模式下 modelValue 可能是误传的单词数组，与点击值是否视为同一选项 */
    singleModelScalar() {
      const raw = this.modelValue
      if (Array.isArray(raw)) {
        return raw.length ? raw[0] : ''
      }
      return raw
    },
    optionValuesEqual(a, b) {
      if (a === b) return true
      if (typeof a === 'string' && typeof b === 'string') return a.trim() === b.trim()
      if (typeof a === 'number' && typeof b === 'number') return a === b
      if (a != null && b != null && String(a) === String(b)) return true
      return false
    },
    /** 是否为「全部」选项（与多选分支判定一致） */
    isAllOptionTag(value, optionName) {
      const name = String(optionName || '').trim()
      const valueStr = String(value ?? '').toLowerCase().trim()
      return (
        name === '全部' ||
        valueStr === 'all' ||
        value === '' ||
        value === null ||
        value === undefined
      )
    },
    isSelected(value) {
      // 使用严格相等比较，确保能正确匹配
      // 同时处理字符串和数字类型的比较
      return this.selectedValues.some(v => {
        if (v === value) return true
        if (typeof v === 'string' && typeof value === 'string') {
          return v.trim() === value.trim()
        }
        if (typeof v === 'number' && typeof value === 'number') {
          return v === value
        }
        // 接口可能返回数字、sessionStorage 还原成字符串，避免「已选中但点不掉」
        if (v != null && value != null && String(v) === String(value)) return true
        return false
      })
    },
    handleClick(value, optionName) {
      if (this.single) {
        const current = this.singleModelScalar()
        if (this.optionValuesEqual(current, value)) {
          // 需求：「全部」选中后再次点击「全部」不取消；其它选项仍可点击第二次清空
          if (this.isAllOptionTag(value, optionName)) {
            return
          }
          this.$emit('update:modelValue', '')
        } else {
          this.$emit('update:modelValue', value)
        }
        return
      }
      
      // 多选模式：保持原有逻辑
      let newValues = [...this.selectedValues]
      const name = String(optionName || '').trim()
      const valueStr = String(value || '').toLowerCase().trim()
      const isAllOption = this.isAllOptionTag(value, optionName)
      const isOtherOption = name === '其他' || 
                            valueStr === 'other' ||
                            valueStr === 'others'
      
      if (isAllOption) {
        // 点击「全部」：未选中则只选全部；已选中则不允许通过再次点击取消
        if (this.isSelected(value)) {
          return
        }
        newValues = [value]
      } else if (isOtherOption) {
        // 如果点击的是"其他"
        if (this.isSelected(value)) {
          // 如果已经选中，则取消选择
          newValues = newValues.filter(v => v !== value)
        } else {
          // 如果未选中，则只选择"其他"（清空其他所有选项）
          // 移除"全部"选项的值
          if (this.allOption) {
            const allValue = this.allOption.option_value
            newValues = newValues.filter(v => v !== allValue)
          }
          // 移除其他所有选项，只保留"其他"
          newValues = [value]
        }
      } else {
        // 如果点击的是其他选项
        if (this.isSelected(value)) {
          // 如果已经选中，则取消选择
          newValues = newValues.filter(v => v !== value)
        } else {
          // 如果未选中，则添加选择（同时移除"全部"和"其他"）
          // 移除"全部"选项的值
          if (this.allOption) {
            const allValue = this.allOption.option_value
            newValues = newValues.filter(v => v !== allValue)
          }
          // 移除"其他"选项的值
          if (this.otherOption) {
            const otherValue = this.otherOption.option_value
            newValues = newValues.filter(v => v !== otherValue)
          }
          // 避免重复添加
          if (!newValues.includes(value)) {
            newValues.push(value)
          }
        }
      }
      
      this.$emit('update:modelValue', newValues)
    },
    toggleExpand() {
      this.isExpanded = !this.isExpanded
    },
    checkMobile() {
      this.isMobile = isMobileDevice()
    }
  }
}
</script>

<style scoped>
.tag-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  min-height: 28px;
}

.filter-tag {
  padding: 0 12px;
  height: 28px;
  line-height: 28px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  border: 1px solid #d9d9d9;
  background: #fff;
  color: rgba(0, 0, 0, 0.85);
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.filter-tag:hover {
  color: #1890ff;
  border-color: #1890ff;
}

.filter-tag-active {
  background: #1890ff !important;
  color: #fff !important;
  border-color: #1890ff !important;
}

.filter-tag-active :deep(.ant-tag) {
  background: #1890ff !important;
  color: #fff !important;
  border-color: #1890ff !important;
}

.filter-tag-active :deep(.ant-tag-content) {
  color: #fff !important;
}

.more-button {
  height: 28px;
  padding: 0 12px;
  color: #1890ff;
  font-size: 14px;
}

.more-button:hover {
  color: #40a9ff;
  background: #e6f7ff;
}

/* 移动端适配 */
@media (max-width: 767px) {
  .tag-filter {
    gap: 10px;
    min-height: 36px;
  }

  .filter-tag {
    padding: 0 16px;
    height: 36px;
    line-height: 36px;
    font-size: 15px;
    border-radius: 6px;
    min-width: 60px;
  }

  .filter-tag-active {
    background: #1890ff !important;
    color: #fff !important;
    border-color: #1890ff !important;
  }

  .filter-tag-active :deep(.ant-tag) {
    background: #1890ff !important;
    color: #fff !important;
    border-color: #1890ff !important;
  }

  .filter-tag-active :deep(.ant-tag-content) {
    color: #fff !important;
  }

  .more-button {
    height: 36px;
    padding: 0 16px;
    font-size: 15px;
  }
}
</style>

